/** @file timelineHandlers: what each `timelineBindings` id does, over the store and the pose clipboard. */
import type { EditorStore } from '../../state/editorTypes';
import { currentClip } from './currentClip';
import { deleteKeysCommand } from './deleteKeysCommand';
import { keyFrame } from './keyFrame';
import { pastePose } from './pastePose';
import type { PoseClipboard } from './poseClipboard';

/** Handlers keyed by binding id, for `registerBindings('timeline', timelineBindings, timelineHandlers(...))`. */
export function timelineHandlers(store: EditorStore, clipboard: PoseClipboard): Record<string, () => void> {
  const st = () => store.getState();
  const seek = (to: (frame: number, last: number) => number) => (): void => {
    const cur = currentClip(st());
    if (cur === null) return;
    const frame = Math.min(cur.clip.frames, Math.max(0, to(st().playhead.frame, cur.clip.frames)));
    st().setPlayhead({ frame, playing: false });
  };
  return {
    'timeline.prevFrame': seek((f) => f - 1),
    'timeline.nextFrame': seek((f) => f + 1),
    'timeline.firstFrame': seek(() => 0),
    'timeline.lastFrame': seek((_, last) => last),
    'timeline.keyPose': () => { keyFrame(store, st().playhead.frame, st().selection.nodes); },
    'timeline.keyAll': () => { keyFrame(store, st().playhead.frame); },
    'timeline.deleteKeys': () => {
      const cur = currentClip(st());
      const command = cur === null ? null : deleteKeysCommand(cur.name, cur.clip, st().selection.keys);
      if (command === null) return;
      void st().dispatch([command], 'delete keys');
      st().select({ keys: [] });
    },
    'timeline.copyPose': () => {
      const clip = st().playhead.clip;
      if (clip !== null) clipboard.current = { clip, frame: st().playhead.frame };
    },
    'timeline.pastePose': () => { pastePose(store, clipboard, st().playhead.frame); },
    'timeline.playPause': () => { st().setPlayhead({ playing: !st().playhead.playing }); },
  };
}
